import { useState, useEffect } from "react";
import { DollarSign, Clock, CheckCircle2, Calculator, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function PayrollPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [payrolls, setPayrolls] = useState([]);
  const [period, setPeriod] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; });
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const fetch = () => {
    setLoading(true);
    api.get("/payroll", { params: { period } }).then((r) => setPayrolls(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetch(); }, [period]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/payroll/generate?period=${period}`);
      alert(`Folha gerada para ${data.generated} operador(es)`);
      fetch();
    } catch (e) { alert(e.response?.data?.detail || "Erro"); }
    setGenerating(false);
  };

  const markPaid = async (id) => {
    await api.put(`/payroll/${id}/pay`);
    fetch();
  };

  const totalSalary = payrolls.reduce((s, p) => s + p.total_salary, 0);
  const pendingCount = payrolls.filter((p) => p.status === "pending").length;
  const paidCount = payrolls.filter((p) => p.status === "paid").length;

  return (
    <div data-testid="payroll-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="crm-heading">Folha de Pagamento</h1>
        {isAdmin && (
          <button onClick={handleGenerate} disabled={generating} className="crm-btn-primary">
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />} Gerar Folha
          </button>
        )}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        <div className="crm-card p-5">
          <p className="text-xs font-semibold uppercase" style={{ color: "var(--crm-text-muted)" }}>Total Folha</p>
          <p className="text-2xl font-bold mt-1" style={{ color: "var(--crm-text)" }}>¥{totalSalary.toLocaleString()}</p>
        </div>
        <div className="crm-card p-5">
          <p className="text-xs font-semibold uppercase" style={{ color: "var(--crm-text-muted)" }}>Operadores</p>
          <p className="text-2xl font-bold mt-1" style={{ color: "var(--crm-text)" }}>{payrolls.length}</p>
        </div>
        <div className="crm-card p-5">
          <p className="text-xs font-semibold uppercase" style={{ color: "var(--crm-text-muted)" }}>Pendentes</p>
          <p className="text-2xl font-bold mt-1" style={{ color: pendingCount ? "#f97316" : "var(--crm-text)" }}>{pendingCount}</p>
        </div>
        <div className="crm-card p-5">
          <p className="text-xs font-semibold uppercase" style={{ color: "var(--crm-text-muted)" }}>Pagos</p>
          <p className="text-2xl font-bold mt-1" style={{ color: "#10b981" }}>{paidCount}</p>
        </div>
      </div>

      {/* Filter */}
      <div className="mb-6">
        <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="crm-input" style={{ maxWidth: "200px" }} />
      </div>

      {/* Table */}
      <div className="space-y-4">
        {loading ? (
          [...Array(2)].map((_, i) => <div key={i} className="h-40 crm-card animate-pulse" />)
        ) : payrolls.length === 0 ? (
          <div className="crm-card p-14 text-center" style={{ color: "var(--crm-text-dim)" }}>
            Nenhuma folha encontrada para {period}
            {isAdmin && <p className="text-xs mt-2">Clique em "Gerar Folha" para criar</p>}
          </div>
        ) : payrolls.map((p) => (
          <div key={p.id} className="crm-card overflow-hidden" data-testid={`payroll-${p.id}`}>
            {/* Header */}
            <div className="px-6 py-4 flex items-center justify-between" style={{ borderBottom: "1px solid var(--crm-border)" }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold" style={{ background: "var(--crm-accent-subtle)", color: "var(--crm-accent)" }}>
                  {p.operator_name?.charAt(0)?.toUpperCase()}
                </div>
                <div>
                  <p className="text-base font-bold" style={{ color: "var(--crm-text)" }}>{p.operator_name}</p>
                  <p className="text-xs" style={{ color: "var(--crm-text-muted)" }}>{p.period}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold`}
                  style={{ background: p.status === "paid" ? "rgba(16,185,129,0.12)" : "rgba(249,115,22,0.12)", color: p.status === "paid" ? "#10b981" : "#f97316" }}>
                  {p.status === "paid" ? <><CheckCircle2 className="w-3.5 h-3.5" /> PAGO</> : <><Clock className="w-3.5 h-3.5" /> PENDENTE</>}
                </span>
                {isAdmin && p.status === "pending" && (
                  <button onClick={() => markPaid(p.id)} className="inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-xl"
                    style={{ color: "white", background: "#10b981", boxShadow: "0 2px 8px rgba(16,185,129,0.25)" }}>
                    <CheckCircle2 className="w-4 h-4" /> Pagar
                  </button>
                )}
              </div>
            </div>

            {/* Breakdown */}
            <div className="px-6 py-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <p className="text-[11px] font-semibold uppercase" style={{ color: "var(--crm-text-dim)" }}>Salário Base</p>
                <p className="text-base font-bold" style={{ color: "var(--crm-text)" }}>¥{p.base_salary.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase" style={{ color: "var(--crm-text-dim)" }}>Horas ({p.hours_worked}h)</p>
                <p className="text-base font-bold" style={{ color: "var(--crm-text)" }}>¥{p.hourly_pay.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase" style={{ color: "var(--crm-text-dim)" }}>Comissões</p>
                <p className="text-base font-bold" style={{ color: p.commissions > 0 ? "var(--crm-accent)" : "var(--crm-text)" }}>¥{p.commissions.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase" style={{ color: "var(--crm-text-dim)" }}>Bônus</p>
                <p className="text-base font-bold" style={{ color: "var(--crm-text)" }}>¥{p.bonus.toLocaleString()}</p>
              </div>
            </div>

            {/* Total */}
            <div className="px-6 py-4 flex justify-between items-center" style={{ background: "var(--crm-accent-subtle)", borderTop: "2px solid var(--crm-accent)" }}>
              <span className="text-xs font-bold uppercase tracking-wider" style={{ color: "var(--crm-text-muted)" }}>Total</span>
              <span className="text-2xl font-extrabold" style={{ color: "var(--crm-text)" }}>¥{p.total_salary.toLocaleString()}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
